import React, { useState } from 'react';

export default function ChannelSidebar({
    channels = [],
    selectedChannel,
    onSelect,
    onCreateChannel
}) {
    const [search, setSearch] = useState('');
    const [filter, setFilter] = useState('all');

    const filtered = channels.filter(ch => {
        const matchesSearch = (ch.name || "")
            .toLowerCase()
            .includes(search.toLowerCase());

        const matchesFilter =
            filter === 'all' || ch.type === filter;

        return matchesSearch && matchesFilter;
    });

    // Grouping logic - use category if exists, otherwise "Other"
    const grouped = filtered.reduce((acc, ch) => {
        const cat = ch.category || 'Other';
        if (!acc[cat]) acc[cat] = [];
        acc[cat].push(ch);
        return acc;
    }, {});

    const categories = Object.keys(grouped).sort();
    const [expandedCategories, setExpandedCategories] = useState(categories);

    const toggleCategory = (cat) => {
        setExpandedCategories(prev =>
            prev.includes(cat)
                ? prev.filter(c => c !== cat)
                : [...prev, cat]
        );
    };

    return (
        <aside className="w-[260px] h-full bg-[#12121E] border-r border-white/5 flex flex-col overflow-hidden">

            {/* Header */}
            <div className="px-4 py-4 border-b border-white/5">
                <div className="flex items-center justify-between mb-3">
                    <h2 className="text-sm font-semibold text-[#F0EFFF]">
                        All Channels
                    </h2>
                    <span className="text-xs text-[#5C5C8A] bg-white/5 px-2 py-0.5 rounded-full font-bold">
                        {channels.length}
                    </span>
                </div>

                {/* Search */}
                <div className="flex items-center gap-2 bg-[#0C0C14] border border-white/5 rounded-lg px-3 py-2 focus-within:border-indigo-500/50 transition-colors">
                    <span className="text-[#5C5C8A] text-xs">🔍</span>
                    <input
                        type="text"
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                        placeholder="Find channel..."
                        className="flex-1 bg-transparent text-xs text-[#F0EFFF] placeholder-[#5C5C8A] focus:outline-none placeholder:font-medium"
                    />
                </div>

                {/* Filters */}
                <div className="flex gap-1.5 mt-3">
                    {['all', 'public', 'private'].map(f => (
                        <button
                            key={f}
                            onClick={() => setFilter(f)}
                            className={`px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider transition-all ${filter === f
                                ? 'bg-indigo-500 text-white shadow-lg shadow-indigo-500/20'
                                : 'text-[#5C5C8A] bg-white/5 hover:bg-white/10'
                                }`}
                        >
                            {f}
                        </button>
                    ))}
                </div>
            </div>

            {/* Channel List */}
            <div className="flex-1 overflow-y-auto py-2 custom-scrollbar">
                {categories.length === 0 ? (
                    <div className="px-4 py-8 text-center text-xs text-gray-500 italic">
                        No channels found
                    </div>
                ) : (
                    categories.map(cat => {
                        const catChannels = grouped[cat] || [];
                        const isExpanded = expandedCategories.includes(cat);

                        return (
                            <div key={cat} className="mb-2">
                                {/* Category */}
                                <button
                                    onClick={() => toggleCategory(cat)}
                                    className="w-full flex justify-between px-4 py-2 text-[10px] font-bold text-[#5C5C8A] uppercase tracking-widest hover:text-gray-400 transition-colors"
                                >
                                    <span>{cat}</span>
                                    <span>{catChannels.length}</span>
                                </button>

                                {/* Channels */}
                                {isExpanded &&
                                    catChannels.map(ch => (
                                        <button
                                            key={ch._id || ch.id}
                                            onClick={() => onSelect(ch)}
                                            className={`w-full flex items-center gap-3 px-4 py-2.5 transition-all ${selectedChannel?._id === ch._id || selectedChannel?.id === ch.id
                                                ? 'bg-indigo-500/10 text-white border-r-2 border-indigo-500'
                                                : 'text-[#9B9BC4] hover:bg-white/5 hover:text-white'
                                                }`}
                                        >
                                            <span className="opacity-40 font-bold w-4 text-center">
                                                {ch.isPrivate || ch.type === 'private' ? '🔒' : '#'}
                                            </span>

                                            <span className="flex-1 text-sm font-medium truncate text-left">
                                                {ch.name}
                                            </span>

                                            {/* Badges */}
                                            {ch.unread > 0 && (
                                                <span className="text-[10px] bg-indigo-500 text-white px-1.5 py-0.5 rounded-full font-bold">
                                                    {ch.unread}
                                                </span>
                                            )}
                                        </button>
                                    ))}
                            </div>
                        );
                    })
                )}
            </div>

            {/* Create Button */}
            <div className="p-4 border-t border-white/5">
                <button
                    onClick={onCreateChannel}
                    className="w-full py-2.5 border border-dashed border-indigo-500/30 text-indigo-400 hover:text-indigo-300 hover:border-indigo-500/50 rounded-xl text-xs font-bold transition-all uppercase tracking-widest"
                >
                    + Create Channel
                </button>
            </div>
        </aside>
    );
}